/* Core & libraries */
import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from "react-jss";
import { Formik, Field, ErrorMessage } from "formik";
import Image from "react-bootstrap/Image";
import { FormattedMessage, defineMessages, useIntl } from "react-intl";

/* Helpers, constants, custom methods, internal libraries */
import {
  createValidationPolicyYupSchema,
  useValidationService
} from "../../resources/validations/validations.js";
import { PAGE_TITLES, DELTA_SKYMILESENROLL_LINK, DELTA_FORGOTLOGIN_LINK, DELTA_PRIVACYPOLICY_LINK } from "../../constants/appConstants";
import { useAnalytics } from "../../hooks/useAnalytics";
import { useAirlineProgramsService } from "../../services/airlineProgramsService.js";
import { useUserContext } from "../../contexts/userContext";

/* Components */
import UCButton from "../buttons/index.js";
import DeltaWhiteLogo from "../../resources/images/delta-white-logo.svg";
import DeltaBusinessText from "../../resources/images/delta-business-text.svg";
import DeltaTermsModal from "../../components/modals/deltaTermsModal"

/* Styles */
import styles from "./styles.js";

const messages = defineMessages({
  firstName: {
    id: "airlineProgram.deltaAirlines.programPage.firstName"
  },
  lastName: {
    id: "airlineProgram.deltaAirlines.programPage.lastName"
  },
  skyMilesNumber: {
    id: "airlineProgram.deltaAirlines.programPage.skyMilesNumber"
  }
})

const DeltaJoinProgramContent = () => {
  const theme = useTheme();
  const classes = styles({ theme });
  const formRef = useRef(null);
  const intl = useIntl();

  const userContext = useUserContext();
  const { getAirlineProgramsData, airlineProgramsData, joinDeltaAirlines } = useAirlineProgramsService();
  const { ucJoinDeltaMockedApiValidationObj } = useValidationService();
  const { initAnalytics } = useAnalytics();

  const [deltaAirlinesSupplier, setDeltaAirlinesSupplier] = useState({});
  const [testSchema, setTestSchema] = useState(null);
  const [showTermsModal, setShowShowTermsModal] = useState(false);
  const [nameError, setNameError] = useState(false);
  const [previousNameError, setPreviousNameError] = useState("");
  const companyName = userContext?.userInfo?.companyInfo?.companyName;

  useEffect(() => {
    const result = createValidationPolicyYupSchema(
      ucJoinDeltaMockedApiValidationObj
    );
    setTestSchema(result);
  }, []);

  useEffect(() => {
    (async () => {
      await getAirlineProgramsData();
    })();
    const initUtagData = {
      channel: "content",
      pageName: PAGE_TITLES["deltaAirlines"],
    };
    initAnalytics(initUtagData, "init call", "view");
  }, []);

  useEffect(() => {
    setDeltaAirlinesSupplier(
      airlineProgramsData?.suppliers?.filter(
        (supplier) => supplier.providerName === "Delta Airlines"
      )[0]
    );
  }, [airlineProgramsData]);

  const handleFormSubmit = () => {
    if (formRef.current) {
      formRef.current.handleSubmit();
    }
  };

  const submitForm = async (values, { setSubmitting, setErrors }) => {
    try {
      const success = await joinDeltaAirlines(
        values,
        deltaAirlinesSupplier?.providerEntityID
      );
      if (
        success.status === "ERROR" &&
        success?.errors[0]?.errorCode === "1000"
      ) {
        setNameError(true);
        setPreviousNameError(values?.membershipNumber);
      } else {
        setErrors({
          apiError: <FormattedMessage id={"airlineProgram.deltaAirlines.programPage.failedApiError"} />
        });
      }
    } catch (error) {
      setErrors({
        apiError: <FormattedMessage id={"airlineProgram.deltaAirlines.programPage.unexpectedError"} />
      });
    } finally {
      setSubmitting(false);
    }
  };

  const checkInputValue = (values) => {
    if (nameError) {
      if (values.membershipNumber !== previousNameError) {
        setNameError(false);
      }
    }
  };

  return (
    <div className={classes.dlProgramContent}>
      {/* Top Bar */}
      <header className={classes.topBar}>
        <div className={classes.contentWrapper}>
          <div className={classes.businessText}>
            <Image src={DeltaBusinessText} />
          </div>
          <div className={classes.deltaLogo}>
            <Image src={DeltaWhiteLogo} />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className={classes.mainContent}>
        <div className={classes.topSection}>
          <h1>
            <FormattedMessage id="airlineProgram.deltaAirlines.programPage.welcome" />
          </h1>
          <p className={classes.invitationText}>
            <FormattedMessage
              id="airlineProgram.deltaAirlines.programPage.invitationText"
              values={{
                companyName: companyName,
              }}
            />          </p>
        </div>
        <div className={classes.formSection}>
          <p>
            <FormattedMessage id="airlineProgram.deltaAirlines.programPage.linkSkymilesText" />
          </p>
          <p>
            <FormattedMessage
              id="airlineProgram.deltaAirlines.programPage.skymilesEnrollmentText"
              values={{
                link: (
                  <a href={DELTA_SKYMILESENROLL_LINK} target="_blank">
                    <FormattedMessage id="airlineProgram.deltaAirlines.programPage.skymilesEnrollmentLinkText" />
                  </a>
                ),
              }}
            />
          </p>
          {testSchema && (
            <Formik
              innerRef={formRef}
              initialValues={{
                firstName: userContext?.userInfo?.firstName || "",
                lastName: userContext?.userInfo?.lastName || "",
                membershipNumber: "",
                acceptTerms: false,
                acceptPrivacyPolicy: false,
              }}
              validationSchema={testSchema}
              onSubmit={(values, formikHelpers) => {
                submitForm(values, formikHelpers);
              }}
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                handleSubmit,
                isSubmitting,
              }) => (
                <form
                  onSubmit={handleSubmit}
                  className={classes.form}
                  onChange={checkInputValue(values)}
                >
                  <div className={classes.formGroup}>
                    <Field
                      type="text"
                      name="firstName"
                      placeholder={intl.formatMessage(messages.firstName)}
                      className={
                        errors.firstName && touched.firstName
                          ? classes.inputError
                          : classes.input
                      }
                      disabled
                    />
                    <ErrorMessage
                      name="firstName"
                      component="span"
                      className={classes.error}
                    />

                    <Field
                      type="text"
                      name="lastName"
                      placeholder={intl.formatMessage(messages.lastName)}
                      className={
                        errors.lastName && touched.lastName
                          ? classes.inputError
                          : classes.input
                      }
                      disabled
                    />
                    <ErrorMessage
                      name="lastName"
                      component="span"
                      className={classes.error}
                    />
                    <div className={classes.formErrorContainer}>
                      <Field
                        type="text"
                        name="membershipNumber"
                        placeholder={intl.formatMessage(messages.skyMilesNumber)}
                        className={
                          errors.membershipNumber && touched.membershipNumber
                            ? `${classes.input} ${classes.inputError}`
                            : classes.input
                        }
                      />
                      {errors.membershipNumber && touched.membershipNumber && (
                        <span className={classes.formErrorTooltip}>
                          {errors.membershipNumber}
                        </span>
                      )}
                    </div>
                    <ErrorMessage
                      name="membershipNumber"
                      component="span"
                      className={classes.error}
                    />
                  </div>
                  {nameError && (
                    <div className={classes.errorSkyMilesText}>
                      <FormattedMessage id="airlineProgram.deltaAirlines.programPage.nameError" />
                    </div>
                  )}

                  <div className={classes.forgotSkyMilesText}>
                    <FormattedMessage
                      id="airlineProgram.deltaAirlines.programPage.forgotSkymilesAccount"
                      values={{
                        link: (
                          <a href={DELTA_FORGOTLOGIN_LINK} target="_blank">
                            <FormattedMessage id="airlineProgram.deltaAirlines.programPage.forgotSkymilesAccountLinkText" />
                          </a>
                        ),
                      }}
                    />
                  </div>

                  <div className={classes.checkboxes}>
                    <label>
                      <Field type="checkbox" name="acceptTerms" />
                      <FormattedMessage
                        id="airlineProgram.deltaAirlines.programPage.acceptTerms"
                        values={{
                          termsLink: (
                            <a
                              href="#"
                              onClick={(e) => {
                                e.preventDefault(); // Prevent the default link behavior
                                setShowShowTermsModal(true); // Show the modal
                              }}
                            >
                              <FormattedMessage id="airlineProgram.deltaAirlines.programPage.acceptTermsLinkText" />
                            </a>
                          ),
                        }}
                      />
                    </label>
                    <label>
                      <Field type="checkbox" name="acceptPrivacyPolicy" />
                      <FormattedMessage
                        id="airlineProgram.deltaAirlines.programPage.acceptPrivacyPolicy"
                        values={{
                          privacyPolicyLink: (
                            <a href={DELTA_PRIVACYPOLICY_LINK} target="_blank">
                              <FormattedMessage id="airlineProgram.deltaAirlines.programPage.acceptPrivacyPolicyLinkText" />
                            </a>
                          ),
                        }}
                      />
                    </label>
                  </div>
                  <div className={classes.registerButtonContainer}>
                    <UCButton
                      type="submit"
                      className={classes.submitButton}
                      disabled={
                        isSubmitting ||
                        !values.firstName ||
                        !values.lastName ||
                        !values.membershipNumber ||
                        !values.acceptTerms ||
                        !values.acceptPrivacyPolicy
                      }
                      onClick={handleFormSubmit}
                    >
                      <FormattedMessage id="airlineProgram.deltaAirlines.programPage.registerButton" />
                    </UCButton>
                  </div>
                </form>
              )}
            </Formik>
          )}
        </div>
      </div>

      <DeltaTermsModal
        showModal={showTermsModal}
        setShowModal={setShowShowTermsModal}
      />
    </div>
  );
};

export default DeltaJoinProgramContent;